(() => {
  if (customElements.get('character-profile')) return;

  class CharacterProfile extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const data = this.querySelector('[data-cp-records]');
      try {
        this.records = JSON.parse(data?.textContent || '[]');
      } catch (error) {
        this.records = [];
      }

      this.drawRecords = this.records.filter((record) => record.drawEnabled && record.discountCode && record.productUrl);
      this.storageKey = this.dataset.storageKey || 'bs-character-profile-draw';
      this.allowRedraw = this.dataset.allowRedraw === 'true';
      this.pageTitle = this.dataset.pageTitle || 'Character Profile';
      this.initialDocumentTitle = document.title;
      this.currentWinner = null;
      this.revealTimer = 0;

      this.archive = this.querySelector('[data-cp-archive]');
      this.profile = this.querySelector('[data-cp-profile]');
      this.invite = this.querySelector('[data-cp-invite]');
      this.reveal = this.querySelector('[data-cp-reveal]');
      this.pack = this.querySelector('[data-cp-pack]');
      this.reward = this.querySelector('[data-cp-reward]');
      this.status = this.querySelector('[data-cp-status]');
      this.copyButton = this.querySelector('[data-cp-copy-code]');
      this.copyLabel = this.copyButton?.textContent.trim() || 'COPY';
      this.storySection = this.querySelector('[data-cp-story-section]');
      this.storyToggle = this.querySelector('[data-cp-story-toggle]');
      this.storyMobileQuery = window.matchMedia('(max-width: 749px)');

      this.bindEvents();
      this.setupStoryToggle();
      this.renderInitialRoute();
      this.scheduleInvite();
    }

    bindEvents() {
      this.querySelectorAll('[data-cp-profile-id]').forEach((button) => {
        button.addEventListener('click', () => this.showProfile(button.dataset.cpProfileId));
      });

      this.querySelector('[data-cp-back]')?.addEventListener('click', () => this.showArchive());
      this.querySelectorAll('[data-cp-open-draw]').forEach((button) => {
        button.addEventListener('click', () => this.openInvite());
      });
      this.querySelectorAll('[data-cp-close-invite]').forEach((button) => {
        button.addEventListener('click', () => this.closeInvite());
      });
      this.querySelector('[data-cp-launch-draw]')?.addEventListener('click', () => {
        this.closeInvite();
        this.openReveal();
      });
      this.querySelectorAll('[data-cp-close-reveal]').forEach((button) => {
        button.addEventListener('click', () => this.closeReveal());
      });
      this.querySelector('[data-cp-open-pack]')?.addEventListener('click', () => this.openPack());
      this.querySelector('[data-cp-copy-code]')?.addEventListener('click', () => this.copyCode());
      this.querySelector('[data-cp-view-winner]')?.addEventListener('click', () => {
        if (!this.currentWinner) return;
        this.closeReveal();
        this.showProfile(this.currentWinner.id);
      });

      this.querySelector('[data-cp-related]')?.addEventListener('click', (event) => {
        const card = event.target.closest('[data-cp-related-id]');
        if (card) this.showProfile(card.dataset.cpRelatedId);
      });

      window.addEventListener('popstate', () => this.renderInitialRoute(false));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.reveal?.hidden) this.closeReveal();
        else if (!this.invite?.hidden) this.closeInvite();
      });

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const blockButton = this.querySelector(`[data-cp-profile-id="${event.detail.blockId}"]`);
        if (blockButton) this.showProfile(event.detail.blockId, false);
      });
    }

    setupStoryToggle() {
      if (!this.storySection || !this.storyToggle) return;

      const syncMode = () => {
        const mobile = this.storyMobileQuery.matches;
        const expanded = mobile ? this.storySection.dataset.userToggled === 'true' : true;
        this.storySection.classList.toggle('is-open', expanded);
        this.storyToggle.setAttribute('aria-expanded', String(expanded));
        this.storyToggle.tabIndex = mobile ? 0 : -1;
      };

      this.storyToggle.addEventListener('click', () => {
        if (!this.storyMobileQuery.matches) return;
        const expanded = !this.storySection.classList.contains('is-open');
        this.storySection.dataset.userToggled = 'true';
        this.storySection.classList.toggle('is-open', expanded);
        this.storyToggle.setAttribute('aria-expanded', String(expanded));
      });

      this.storyMobileQuery.addEventListener?.('change', syncMode);
      this.syncStoryMode = syncMode;
      syncMode();
    }

    renderInitialRoute(scroll = false) {
      const id = new URLSearchParams(window.location.search).get('character');
      if (id && this.records.some((record) => record.id === id)) {
        this.showProfile(id, false, scroll);
      } else {
        this.showArchive(false, scroll);
      }
    }

    showProfile(id, push = true, scroll = true) {
      const record = this.records.find((item) => item.id === id);
      if (!record) return;

      this.archive.hidden = true;
      this.profile.hidden = false;
      this.populateProfile(record);
      if (this.storySection) {
        this.storySection.dataset.userToggled = 'false';
        this.syncStoryMode?.();
      }
      document.title = `${record.name} | ${this.initialDocumentTitle}`;

      if (push) {
        const url = new URL(window.location.href);
        url.searchParams.set('character', record.id);
        window.history.pushState({ character: record.id }, '', url);
      }
      if (scroll) window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    showArchive(push = true, scroll = true) {
      this.archive.hidden = false;
      this.profile.hidden = true;
      document.title = this.initialDocumentTitle;

      if (push) {
        const url = new URL(window.location.href);
        url.searchParams.delete('character');
        window.history.pushState({}, '', url);
      }
      if (scroll) window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    populateProfile(record) {
      const image = this.querySelector('[data-cp-detail-image]');
      const placeholder = this.querySelector('[data-cp-detail-placeholder]');
      if (record.profileImage) {
        image.src = record.profileImage;
        image.alt = `${record.name} character profile`;
        image.hidden = false;
        placeholder.hidden = true;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
        placeholder.hidden = false;
      }

      this.querySelector('[data-cp-name]').textContent = record.name || '';
      this.querySelector('[data-cp-role]').textContent = record.role || '';
      const mobileName = this.querySelector('[data-cp-mobile-name]');
      const mobileRole = this.querySelector('[data-cp-mobile-role]');
      if (mobileName) mobileName.textContent = record.name || '';
      if (mobileRole) mobileRole.textContent = record.role || '';
      this.querySelector('[data-cp-story]').innerHTML = record.storyHtml || '';
      const signature = this.querySelector('[data-cp-signature]');
      if (signature) signature.textContent = record.name || '';

      const specs = this.querySelector('[data-cp-specs]');
      specs.innerHTML = (record.info || []).map((item) => {
        if (item.label !== 'Related Products') {
          return `<dl class="cp-profile__spec"><dt>${this.escape(item.label)}</dt><dd>${this.escape(item.value)}</dd></dl>`;
        }

        const products = [...String(item.value || '').matchAll(/<a[^>]+href="([^"]+)"[^>]*>(.*?)<\/a>/g)]
          .map(([, href, name]) => ({
            href: this.escape(href),
            name: this.escape(String(name).replace(/<[^>]*>/g, ''))
          }));
        const productMarkup = products.map((product) => (
          `<a class="product-item product-link" href="${product.href}"><span class="product-name">${product.name}</span></a>`
        )).join('');
        return `<dl class="cp-profile__spec get-her-row"><dt><span class="get-her-button">GET HER NOW</span></dt><dd class="product-list">${productMarkup}</dd></dl>`;
      }).join('');

      const visualStory = this.querySelector('[data-cp-visual-story]');
      const visualStoryImage = this.querySelector('[data-cp-visual-story-image]');
      if (record.visualStoryImage) {
        visualStoryImage.src = record.visualStoryImage;
        visualStoryImage.alt = `${record.name} visual story`;
        visualStory.hidden = false;
      } else {
        visualStoryImage.removeAttribute('src');
        visualStory.hidden = true;
      }

      this.renderRelated(record.id);
    }

    renderRelated(currentId) {
      const section = this.querySelector('[data-cp-related-section]');
      const container = this.querySelector('[data-cp-related]');
      const related = this.records.filter((record) => record.id !== currentId).slice(0, 3);
      container.innerHTML = related.map((record) => `
        <button class="cp-related__card" type="button" data-cp-related-id="${this.escape(record.id)}">
          ${record.cardImage ? `<img src="${this.escape(record.cardImage)}" alt="${this.escape(record.name)}" loading="lazy">` : ''}
          <span>${this.escape(record.name)}</span>
        </button>
      `).join('');
      section.hidden = related.length === 0;
    }

    scheduleInvite() {
      if (this.dataset.autoInvite !== 'true' || !this.drawRecords.length) return;
      if (new URLSearchParams(window.location.search).has('character')) return;
      const delay = Number(this.dataset.inviteDelay || 0);
      window.setTimeout(() => this.openInvite(), Number.isFinite(delay) ? delay : 0);
    }

    openInvite() {
      if (!this.drawRecords.length) return;
      this.invite.hidden = false;
      this.invite.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cp-modal-open');
      this.querySelector('[data-cp-launch-draw]')?.focus({ preventScroll: true });
    }

    closeInvite() {
      this.invite.hidden = true;
      this.invite.setAttribute('aria-hidden', 'true');
      if (this.reveal.hidden) document.body.classList.remove('cp-modal-open');
    }

    openReveal() {
      if (!this.drawRecords.length) return;
      this.currentWinner = this.getWinner();
      this.resetReveal();
      this.reveal.hidden = false;
      this.reveal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cp-modal-open');
      this.querySelector('[data-cp-open-pack]')?.focus({ preventScroll: true });
    }

    closeReveal() {
      window.clearTimeout(this.revealTimer);
      this.reveal.hidden = true;
      this.reveal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cp-modal-open');
    }

    getWinner() {
      if (!this.allowRedraw) {
        try {
          const savedId = window.localStorage.getItem(this.storageKey);
          const saved = this.drawRecords.find((record) => record.id === savedId);
          if (saved) return saved;
        } catch (error) {
          // Storage can be unavailable in strict privacy mode.
        }
      }

      const winner = this.drawRecords[Math.floor(Math.random() * this.drawRecords.length)];
      if (!this.allowRedraw && winner) {
        try { window.localStorage.setItem(this.storageKey, winner.id); } catch (error) { /* no-op */ }
      }
      return winner;
    }

    resetReveal() {
      window.clearTimeout(this.revealTimer);
      this.pack.dataset.state = 'ready';
      this.reward.hidden = true;
      this.status.textContent = '';

      const image = this.querySelector('[data-cp-reveal-image]');
      const placeholder = this.querySelector('[data-cp-reveal-placeholder]');
      const source = this.currentWinner?.profileImage || this.currentWinner?.cardImage;
      if (source) {
        image.src = source;
        image.alt = `${this.currentWinner.name} character card`;
        image.hidden = false;
        placeholder.hidden = true;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
        placeholder.hidden = false;
      }

      this.copyButton.textContent = this.copyLabel;
      this.copyButton.disabled = false;
    }

    openPack() {
      if (!this.currentWinner || this.pack.dataset.state !== 'ready') return;
      this.pack.dataset.state = 'opening';
      this.status.textContent = 'Opening your character pack...';
      this.revealTimer = window.setTimeout(() => this.finishReveal(), 1550);
    }

    finishReveal() {
      if (!this.currentWinner) return;
      this.pack.dataset.state = 'revealed';
      this.reward.hidden = false;
      this.querySelector('[data-cp-reward-name]').textContent = this.currentWinner.name;
      this.querySelector('[data-cp-reward-label]').textContent = this.currentWinner.discountLabel || 'PRODUCT REWARD';
      this.querySelector('[data-cp-reward-code]').textContent = this.currentWinner.discountCode;

      const discountLink = `/discount/${encodeURIComponent(this.currentWinner.discountCode)}?redirect=${encodeURIComponent(this.currentWinner.productUrl)}`;
      this.querySelector('[data-cp-shop-winner]').href = discountLink;
      this.status.textContent = `${this.currentWinner.name} revealed. Your code is ${this.currentWinner.discountCode}.`;
    }

    async copyCode() {
      if (!this.currentWinner?.discountCode) return;
      const button = this.copyButton;
      try {
        await navigator.clipboard.writeText(this.currentWinner.discountCode);
        button.textContent = 'COPIED';
        button.disabled = true;
        this.status.textContent = 'Discount code copied.';
      } catch (error) {
        button.textContent = this.currentWinner.discountCode;
        this.status.textContent = 'Select the code and copy it manually.';
      }
    }

    escape(value) {
      const span = document.createElement('span');
      span.textContent = value == null ? '' : String(value);
      return span.innerHTML;
    }
  }

  customElements.define('character-profile', CharacterProfile);
})();
